// @ts-check
// Detalhe de um item da agenda (doc 10 do mapeamento, seção 12): quando, onde, responsável, card, participantes com
// a resposta ao convite e as ações (remarcar, cancelar, baixar o .ics, agendar a partir da data do card). Conteúdo
// de agenda conectada não compartilhada aparece como "Não compartilhado", como no Twenty.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { findCalendarEventType } from '../../../../shared/calendar/event-types.js';
import { cancelCalendarEvent, downloadEventInvitation } from '../../data/calendar.js';
import { Link } from '../../app/router.js';
import { patchRecordLocally } from '../../data/record-store.js';
import { formatLongDate, formatTime } from '../../lib/calendar-dates.js';
import { formatRelativeToNow } from '../../lib/format.js';
import { downloadBlob } from '../../lib/csv.js';
import { getTagStyle } from '../../lib/theme-colors.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { ScheduleEventModal } from './schedule-event-modal.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/calendar.js').CalendarItem} CalendarItem */

const RESPONSE_COLORS = /** @type {Record<string, string>} */ ({ NEEDS_ACTION: 'orange', DECLINED: 'red', TENTATIVE: 'yellow', ACCEPTED: 'green' });
const SOURCE_ICONS = /** @type {Record<string, string>} */ ({ GOOGLE: 'IconBrandGoogle', MICROSOFT: 'IconLayoutGrid', OPPORTUNITY: 'IconTargetArrow', SCHOOL_EVENT: 'IconSchool', CRM: 'IconCalendarEvent' });

/** Cor do item: a do tipo; eventos da escola em roxo. @param {CalendarItem} item */
export function calendarItemColor(item) {
  if (item.kind === 'SCHOOL_EVENT') return 'purple';
  if (item.source === 'GOOGLE' || item.source === 'MICROSOFT') return item.eventType === 'OTHER' ? 'sky' : findCalendarEventType(item.eventType)?.color ?? 'sky';
  return findCalendarEventType(item.eventType)?.color ?? 'gray';
}

/** Título exibido: restrito vira "Não compartilhado". @param {CalendarItem} item */
export function calendarItemTitle(item) {
  if (item.isRestricted) return t('calendar.notShared');
  return item.title || t(`calendar.eventTypes.${item.eventType}`) || t('calendar.untitled');
}

/** @param {CalendarItem} item */
export const calendarSourceIcon = (item) => SOURCE_ICONS[item.source] ?? 'IconCalendarEvent';

/**
 * @param {{ label: string, icon: string, children: any }} props
 */
function DetailRow({ label, icon, children }) {
  return html`<div class="calendar-detail-row">
    <span class="calendar-detail-row-label" title=${label}><${Icon} name=${icon} size=${16} /></span>
    <div class="calendar-detail-row-value">${children}</div>
  </div>`;
}

/**
 * @param {{ item: CalendarItem, onClose: () => void, onChanged: () => void }} props
 */
export function CalendarEventDetail({ item, onClose, onChanged }) {
  const [modal, setModal] = useState(/** @type {'reschedule' | 'schedule' | 'cancel' | null} */ (null));
  const [isWorking, setIsWorking] = useState(false);
  const color = calendarItemColor(item);
  const isCrmEvent = item.kind === 'EVENT' && item.source === 'CRM';
  const canChange = isCrmEvent && !item.isCanceled;
  const typeLabel = item.kind === 'SCHOOL_EVENT' ? t('calendar.eventTypes.SCHOOL_EVENT') : t(`calendar.eventTypes.${item.eventType}`);
  const sameDay = new Date(item.startsAt).toDateString() === new Date(item.endsAt).toDateString();

  const cancel = async () => {
    setIsWorking(true);
    try {
      const { invitation } = await cancelCalendarEvent(item.id);
      const fieldName = findCalendarEventType(item.eventType)?.fieldName;
      if (item.enrollmentOpportunity && fieldName) patchRecordLocally('enrollmentOpportunity', item.enrollmentOpportunity.id, { [fieldName]: null });
      showSuccessSnackbar(t('calendar.detail.canceledSnackbar'));
      if (invitation?.status === 'FAILED') enqueueSnackbar({ message: t('calendar.schedule.invitationFailedSnackbar'), variant: 'error' });
      setModal(null);
      onChanged();
      onClose();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsWorking(false);
    }
  };

  const download = async () => {
    try {
      const { blob } = await downloadEventInvitation(item.id);
      downloadBlob(blob, 'evento.ics');
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<aside class="calendar-event-detail" aria-label=${calendarItemTitle(item)} data-testid="calendar-event-detail">
    <div class="calendar-event-detail-header">
      <span class="calendar-event-detail-type" style=${getTagStyle(color)}>
        <${Icon} name=${calendarSourceIcon(item)} size=${14} />${typeLabel}
      </span>
      ${item.isCanceled ? html`<${Tag} color="red" text=${t('calendar.canceled')} />` : null}
      <span class="calendar-event-detail-spacer"></span>
      <${LightIconButton} icon="IconX" ariaLabel=${t('calendar.detail.close')} onClick=${onClose} />
    </div>
    <h2 class="calendar-event-detail-title" data-canceled=${item.isCanceled ? 'true' : undefined}>
      ${item.isRestricted ? html`<span class="calendar-not-shared"><${Icon} name="IconLock" size=${14} />${t('calendar.notShared')}</span>` : calendarItemTitle(item)}
    </h2>
    ${item.isRestricted ? html`<p class="calendar-event-detail-hint">${t('calendar.notSharedHint')}</p>` : null}
    <div class="calendar-event-detail-rows">
      <${DetailRow} label=${t('calendar.detail.when')} icon="IconClock">
        <span>${formatLongDate(item.startsAt)}</span>
        <span class="calendar-detail-secondary">${item.isFullDay
          ? t('calendar.allDay')
          : sameDay ? `${formatTime(item.startsAt)} - ${formatTime(item.endsAt)}` : `${formatTime(item.startsAt)} - ${formatLongDate(item.endsAt)} ${formatTime(item.endsAt)}`}</span>
      <//>
      ${item.location ? html`<${DetailRow} label=${t('calendar.detail.where')} icon="IconMapPin"><span>${item.location}</span><//>` : null}
      ${item.conferenceLink
        ? html`<${DetailRow} label=${t('calendar.detail.conference')} icon="IconVideo">
            <a href=${item.conferenceLink} target="_blank" rel="noopener noreferrer" class="calendar-detail-link">${item.conferenceLink}</a>
          <//>`
        : null}
      ${item.organizer
        ? html`<${DetailRow} label=${t('calendar.detail.organizer')} icon="IconUserCircle">
            <span class="calendar-detail-person"><${Avatar} placeholder=${item.organizer.name || '?'} seed=${item.organizer.workspaceMemberId} size="md" type="rounded" />${item.organizer.name}</span>
          <//>`
        : null}
      ${item.enrollmentOpportunity
        ? html`<${DetailRow} label=${t('calendar.detail.card')} icon="IconTargetArrow">
            <${Link} href=${`/object/enrollmentOpportunity/${item.enrollmentOpportunity.id}`} className="calendar-detail-link">${item.enrollmentOpportunity.name}<//>
          <//>`
        : null}
      ${item.description && !item.isRestricted ? html`<${DetailRow} label=${t('calendar.schedule.notes')} icon="IconAlignLeft"><p class="calendar-detail-description">${item.description}</p><//>` : null}
      ${item.kind === 'EVENT'
        ? html`<${DetailRow} label=${t('calendar.detail.participants')} icon="IconUsers">
            ${item.participants.length === 0
              ? html`<span class="calendar-detail-secondary">${t('calendar.detail.noParticipants')}</span>`
              : html`<ul class="calendar-detail-participants">
                  ${item.participants.map((participant) => {
                    const name = participant.person?.name || participant.workspaceMemberName || participant.displayName || participant.handle || '?';
                    return html`<li key=${`${participant.handle}-${participant.personId}`}>
                      <${Avatar} placeholder=${name} seed=${participant.personId ?? participant.handle ?? name} size="md" type="rounded" />
                      <span class="calendar-detail-participant-name">
                        ${participant.personId ? html`<${Link} href=${`/object/person/${participant.personId}`} className="calendar-detail-link">${name}<//>` : name}
                        ${participant.handle && participant.handle !== name ? html`<span class="calendar-detail-secondary">${participant.handle}</span>` : null}
                      </span>
                      ${participant.isOrganizer
                        ? html`<${Tag} color="gray" text=${t('calendar.detail.organizer')} />`
                        : html`<${Tag} color=${RESPONSE_COLORS[participant.responseStatus] ?? 'gray'} text=${t(`calendar.responses.${participant.responseStatus}`)} />`}
                    </li>`;
                  })}
                </ul>`}
          <//>`
        : null}
      <${DetailRow} label=${t('calendar.detail.source')} icon=${calendarSourceIcon(item)}>
        <span class="calendar-detail-secondary">
          ${t(`calendar.sources.${item.source}`)}${item.connectedAccounts?.length ? ` (${item.connectedAccounts.map((account) => account.handle).join(', ')})` : ''}
        </span>
        ${isCrmEvent
          ? html`<span class="calendar-detail-secondary">${item.invitationSentAt ? t('calendar.detail.invitationSent', { relative: formatRelativeToNow(item.invitationSentAt) }) : t('calendar.detail.invitationNotSent')}</span>`
          : null}
      <//>
      ${item.kind === 'OPPORTUNITY_DATE' ? html`<p class="calendar-event-detail-hint">${t('calendar.detail.cardDateHint')}</p>` : null}
    </div>
    <div class="calendar-event-detail-actions">
      ${item.kind === 'OPPORTUNITY_DATE'
        ? html`<${Button} title=${t('calendar.detail.scheduleFromDate')} icon="IconCalendarPlus" accent="blue" size="small" onClick=${() => setModal('schedule')} />`
        : null}
      ${canChange ? html`<${Button} title=${t('calendar.detail.reschedule')} icon="IconCalendarClock" variant="secondary" size="small" onClick=${() => setModal('reschedule')} />` : null}
      ${item.kind === 'EVENT' && !item.isRestricted ? html`<${Button} title=${t('calendar.detail.downloadIcs')} icon="IconDownload" variant="secondary" size="small" onClick=${download} />` : null}
      ${item.href && item.kind !== 'EVENT'
        ? html`<${Link} href=${item.href} className="button calendar-detail-open" data-variant="secondary" data-size="small">
            <${Icon} name="IconExternalLink" size=${14} /><span class="button-title">${item.kind === 'SCHOOL_EVENT' ? t('calendar.detail.openRecord') : t('calendar.detail.openCard')}</span>
          <//>`
        : null}
      ${canChange ? html`<${Button} title=${t('calendar.detail.cancel')} icon="IconCalendarX" variant="secondary" accent="danger" size="small" onClick=${() => setModal('cancel')} />` : null}
    </div>
    ${modal === 'reschedule' ? html`<${ScheduleEventModal} event=${item} onClose=${() => setModal(null)} onSaved=${() => { onChanged(); onClose(); }} />` : null}
    ${modal === 'schedule' && item.enrollmentOpportunity
      ? html`<${ScheduleEventModal}
          opportunity=${{ id: item.enrollmentOpportunity.id, name: item.enrollmentOpportunity.name, ownerId: item.enrollmentOpportunity.ownerId ?? null }}
          defaultEventType=${item.eventType}
          defaultStartsAt=${item.startsAt}
          onClose=${() => setModal(null)}
          onSaved=${() => { onChanged(); onClose(); }}
        />`
      : null}
    ${modal === 'cancel'
      ? html`<${Modal}
          title=${t('calendar.detail.cancelTitle')}
          subtitle=${t('calendar.detail.cancelSubtitle')}
          confirmLabel=${t('calendar.detail.cancelConfirm')}
          confirmAccent="danger"
          isLoading=${isWorking}
          onClose=${() => setModal(null)}
          onConfirm=${cancel}
        />`
      : null}
  </aside>`;
}
